import React, { useState, useEffect, useMemo } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db } from '../../../firebase'
import '../../../styles/ChooseIndipendentWrestler.css'
import CreditIcon from '../../CreditIcon'
import InjuryIcon from '../../InjuryIcon'
import { PremiumWrestlerCommentButton } from '../../premium/PremiumInfoButton'
import { getBrandBadgeClass } from '../../../utils/brandBadge'
import { usePremium } from '../../../premium/PremiumContext'
import ReactDOM from 'react-dom'

const PRICE_FILTERS = [
  { value: 'all', label: 'Prezzo' },
  { value: '0-50', label: '0-50' },
  { value: '51-100', label: '51-100' },
  { value: '101-150', label: '100+' }
]

export default function ChooseIndependentWrestlers({
  availableWrestlers: originalWrestlers,
  rosterName = '',
  roster = null,
  initialSelection = [],
  onConfirm,
  onBack,
  onCancel
}) {
  const {
    maxWrestlers = 6,
    maxCredits = 300,
    minMaleWrestlers = 0,
    minFemaleWrestlers = 0,
    onlyMainRoster = false,
    onlyNXT = false
  } = roster || {}
  const { isPremium } = usePremium()

  const availableWrestlers = useMemo(
    () =>
      originalWrestlers.map(wrestler => {
        const {
          premiumComment,
          position,
          storyline,
          ...standardWrestler
        } = wrestler

        return {
          ...standardWrestler,
          name: wrestler.name || wrestler.nome || 'Sconosciuto',
          price: wrestler.price || 0,
          score: wrestler.score || 0,
          gender: wrestler.gender || 'Non specificato',
          brand: wrestler.brand || 'N/A',
          active: wrestler.active !== false,
          ...(isPremium ? { premiumComment: premiumComment || '' } : {}),
          isInjured:
            wrestler.isInjured || wrestler.is_injured || wrestler.injured || false
        }
      }),
    [isPremium, originalWrestlers]
  )

  const [selected, setSelected] = useState(initialSelection)
  const [currentUser, setCurrentUser] = useState(null)
  const [favoriteWrestlerIds, setFavoriteWrestlerIds] = useState([])
  const [isMobile, setIsMobile] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [genderFilter, setGenderFilter] = useState('all')
  const [brandFilter, setBrandFilter] = useState('all')
  const [priceFilter, setPriceFilter] = useState('all')
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [filtersCollapsed, setFiltersCollapsed] = useState(true)

  const filteredBrands = useMemo(() => {
    let list = availableWrestlers

    if (onlyMainRoster) {
      list = list.filter(
        wrestler => wrestler.brand === 'Raw' || wrestler.brand === 'SmackDown'
      )
    }
    if (onlyNXT) {
      list = list.filter(wrestler => wrestler.brand === 'NXT')
    }

    return [...new Set(list.map(wrestler => wrestler.brand))].sort()
  }, [availableWrestlers, onlyMainRoster, onlyNXT])

  const resetAllFilters = () => {
    setSearchTerm('')
    setGenderFilter('all')
    setBrandFilter('all')
    setPriceFilter('all')
    setSortBy('name')
    setSortOrder('asc')
  }

  const brandCounts = useMemo(() => {
    const counts = {}

    selected.forEach(wrestler => {
      counts[wrestler.brand] = (counts[wrestler.brand] || 0) + 1
    })

    return counts
  }, [selected])

  const filteredWrestlers = useMemo(() => {
    const list = availableWrestlers.filter(wrestler => {
      if (!wrestler.active) return false

      if (
        onlyMainRoster &&
        wrestler.brand !== 'Raw' &&
        wrestler.brand !== 'SmackDown'
      )
        return false
      if (onlyNXT && wrestler.brand !== 'NXT') return false

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        const nameLower = wrestler.name.toLowerCase()
        if (!nameLower.includes(searchLower)) return false
      }

      if (genderFilter !== 'all' && wrestler.gender !== genderFilter)
        return false
      if (brandFilter !== 'all' && wrestler.brand !== brandFilter) return false

      if (priceFilter !== 'all') {
        const [min, max] = priceFilter.split('-').map(Number)
        const price = wrestler.price || 0
        if (price < min || price > max) return false
      }

      return true
    })

    list.sort((first, second) => {
      let valueA = first[sortBy]
      let valueB = second[sortBy]

      if (valueA == null) valueA = ''
      if (valueB == null) valueB = ''

      if (sortBy === 'name') {
        const stringA = String(valueA).toLowerCase()
        const stringB = String(valueB).toLowerCase()
        return sortOrder === 'asc'
          ? stringA.localeCompare(stringB)
          : stringB.localeCompare(stringA)
      }

      const numA = Number(valueA) || 0
      const numB = Number(valueB) || 0
      return sortOrder === 'asc' ? numA - numB : numB - numA
    })

    return list
  }, [
    availableWrestlers,
    onlyMainRoster,
    onlyNXT,
    searchTerm,
    genderFilter,
    brandFilter,
    priceFilter,
    sortBy,
    sortOrder
  ])

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 992)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      setCurrentUser(user)

      if (!user) {
        setFavoriteWrestlerIds([])
        return
      }

      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid))
        setFavoriteWrestlerIds(
          Array.isArray(userSnap.data()?.favoriteWrestler)
            ? userSnap.data().favoriteWrestler
            : []
        )
      } catch (favoriteError) {
        console.error('Errore caricamento preferiti:', favoriteError)
        setFavoriteWrestlerIds([])
      }
    })

    return () => unsubscribe()
  }, [])

  const spentCredits = selected.reduce(
    (sum, wrestler) => sum + (wrestler.price || 0),
    0
  )
  const remainingCredits = Math.max(0, maxCredits - spentCredits)
  const maleCount = selected.filter(
    wrestler => wrestler.gender === 'Uomo'
  ).length
  const femaleCount = selected.filter(
    wrestler => wrestler.gender === 'Donna'
  ).length
  const selectedIds = useMemo(
    () => new Set(selected.map(wrestler => wrestler.id)),
    [selected]
  )

  const selectionProgress =
    maxWrestlers > 0 ? (selected.length / maxWrestlers) * 100 : 0
  const creditsProgress = maxCredits > 0 ? (spentCredits / maxCredits) * 100 : 0
  const hasActiveFilters = Boolean(
    searchTerm ||
    genderFilter !== 'all' ||
    brandFilter !== 'all' ||
    priceFilter !== 'all'
  )
  const genderRequirementMet =
    maleCount >= minMaleWrestlers && femaleCount >= minFemaleWrestlers

  const toggleWrestler = wrestler => {
    const alreadySelected = selectedIds.has(wrestler.id)

    if (alreadySelected) {
      setSelected(selected.filter(item => item.id !== wrestler.id))
      return
    }

    if ((wrestler.price || 0) > remainingCredits) return
    if (selected.length >= maxWrestlers) return

    setSelected([...selected, wrestler])
  }

  const canConfirm =
    selected.length == maxWrestlers &&
    selected.length <= maxWrestlers &&
    maleCount >= minMaleWrestlers &&
    femaleCount >= minFemaleWrestlers &&
    spentCredits <= maxCredits

  const favoriteFilteredWrestlers = useMemo(
    () =>
      filteredWrestlers.filter(wrestler =>
        favoriteWrestlerIds.includes(wrestler.id)
      ),
    [favoriteWrestlerIds, filteredWrestlers]
  )

  const otherFilteredWrestlers = useMemo(
    () =>
      filteredWrestlers.filter(
        wrestler => !favoriteWrestlerIds.includes(wrestler.id)
      ),
    [favoriteWrestlerIds, filteredWrestlers]
  )

  const wrestlerSections = useMemo(() => {
    if (!currentUser || !favoriteFilteredWrestlers.length) {
      return [{ key: 'all', title: null, wrestlers: filteredWrestlers }]
    }

    return [
      {
        key: 'favorites',
        title: `Preferiti (${favoriteFilteredWrestlers.length})`,
        wrestlers: favoriteFilteredWrestlers
      },
      {
        key: 'others',
        title: `Altri wrestler (${otherFilteredWrestlers.length})`,
        wrestlers: otherFilteredWrestlers
      }
    ].filter(section => section.wrestlers.length > 0)
  }, [
    currentUser,
    favoriteFilteredWrestlers,
    filteredWrestlers,
    otherFilteredWrestlers
  ])

  const summaryItems = [
    {
      label: 'Wrestler scelti',
      value: `${selected.length}/${maxWrestlers}`,
      helper:
        selected.length === maxWrestlers
          ? 'Fazione completa'
          : `${maxWrestlers - selected.length} posti rimasti`,
      icon: 'bi-people-fill'
    },
    {
      label: 'Crediti rimasti',
      value: `${remainingCredits}/${maxCredits}`,
      helper: spentCredits === 0 ? 'Nessuna spesa' : `${spentCredits} spesi`,
      icon: 'bi-wallet2'
    },
    {
      label: 'Vincoli genere',
      value: `${maleCount}/${minMaleWrestlers} - ${femaleCount}/${minFemaleWrestlers}`,
      helper: genderRequirementMet
        ? 'Requisiti rispettati'
        : 'Minimi ancora da completare',
      icon: 'bi-person-badge'
    }
  ]

  const mobileSummaryItems = [
    {
      icon: 'bi-people-fill',
      label: 'Scelti',
      value: `${selected.length}/${maxWrestlers}`
    },
    { icon: 'bi-wallet2', label: 'Crediti', value: remainingCredits },
    {
      icon: 'bi-person-badge',
      label: 'Vincoli',
      value: `${maleCount}/${minMaleWrestlers} · ${femaleCount}/${minFemaleWrestlers}`
    }
  ]

  const filterControls = (
    <div className='ciw-filter-shell'>
      <div className='ciw-search-row'>
        <div className='input-group ciw-search-group'>
          <span className='input-group-text bg-transparent border-secondary'>
            <i className='bi bi-search text-muted-brand'></i>
          </span>
          <input
            type='text'
            className='form-control bg-transparent border-secondary text-light'
            placeholder='Cerca wrestler...'
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
          />
          <button
            type='button'
            className='btn btn-outline-secondary border-secondary'
            onClick={() => setSearchTerm('')}
            disabled={!searchTerm}
            aria-label='Cancella ricerca'
          >
            <i className='bi bi-x-lg'></i>
          </button>
        </div>
      </div>
      <div className='ciw-filter-header'>
        <button
          type='button'
          className='btn btn-sm btn-outline-light ciw-collapse-btn'
          onClick={() => setFiltersCollapsed(current => !current)}
          aria-expanded={!filtersCollapsed}
        >
          <i
            className={`bi ${filtersCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'
              } me-1`}
          ></i>
          {filtersCollapsed ? 'Mostra' : 'Nascondi'}
        </button>
      </div>

      <div className={`ciw-filter-grid ${filtersCollapsed ? 'd-none' : ''}`}>
        <div className='ciw-filter-field'>
          <label className='form-label'>Ordina per</label>
          <select
            className='form-select bg-transparent border-secondary text-light'
            value={sortBy}
            onChange={event => setSortBy(event.target.value)}
          >
            <option value='name'>Nome</option>
            <option value='price'>Prezzo</option>
            <option value='score'>Punti</option>
          </select>
        </div>

        <div className='ciw-filter-field'>
          <label className='form-label'>Ordine</label>
          <select
            className='form-select bg-transparent border-secondary text-light'
            value={sortOrder}
            onChange={event => setSortOrder(event.target.value)}
          >
            <option value='asc'>Crescente</option>
            <option value='desc'>Decrescente</option>
          </select>
        </div>

        <div className='ciw-filter-field'>
          <label className='form-label'>Sesso</label>
          <select
            className='form-select bg-transparent border-secondary text-light'
            value={genderFilter}
            onChange={event => setGenderFilter(event.target.value)}
          >
            <option value='all'>Tutti</option>
            <option value='Uomo'>Uomo</option>
            <option value='Donna'>Donna</option>
          </select>
        </div>

        <div className='ciw-filter-field'>
          <label className='form-label'>Brand</label>
          <select
            className='form-select bg-transparent border-secondary text-light'
            value={brandFilter}
            onChange={event => setBrandFilter(event.target.value)}
          >
            <option value='all'>Tutti</option>
            {filteredBrands.map(brand => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
        </div>

        <div className='ciw-filter-field'>
          <label className='form-label'>Prezzo</label>
          <select
            className='form-select bg-transparent border-secondary text-light'
            value={priceFilter}
            onChange={event => setPriceFilter(event.target.value)}
          >
            {PRICE_FILTERS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type='button'
          className='btn btn-outline-brand-secondary ciw-reset-btn'
          onClick={resetAllFilters}
          disabled={
            !hasActiveFilters && sortBy === 'name' && sortOrder === 'asc'
          }
        >
          <i className='bi bi-arrow-counterclockwise me-1'></i>
          Resetta
        </button>
      </div>
    </div>
  )

  const mobileActionsBar = isMobile ? (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999, // superiore a qualsiasi modal
        backgroundColor: '#0f121f', // colore di sfondo solido
        borderTop: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 -12px 28px rgba(0,0,0,0.32)',
        paddingBottom: 'env(safe-area-inset-bottom)'
      }}
    >
      <div className='container-fluid px-3'>
        <div className='ciw-mobile-footer-shell'>
          {/* contenuto identico a prima */}
          <div className='ciw-mobile-progress-wrap'>
            <div className='ciw-mobile-progress-labels'>
              <span>
                Fazione {selected.length}/{maxWrestlers}
              </span>
              <span>{remainingCredits} crediti</span>
            </div>
            <div className='progress ciw-progress ciw-mobile-progress mt-2'>
              <div
                className={`progress-bar ${spentCredits > maxCredits * 0.9
                    ? 'bg-danger'
                    : 'bg-brand-secondary'
                  }`}
                style={{ width: `${Math.min(creditsProgress, 100)}%` }}
              ></div>
            </div>
          </div>

          <div className='d-flex align-items-center justify-content-between gap-3 ciw-mobile-actions-inner'>
            <button
              type='button'
              className='btn btn-outline-light'
              onClick={onBack}
            >
              <i className='bi bi-arrow-left'></i>
            </button>
            <div className='flex-grow-1 ciw-mobile-summary'>
              <div className='ciw-mobile-metrics'>
                {mobileSummaryItems.map(item => (
                  <div
                    key={item.label}
                    className='ciw-mobile-metric'
                    title={item.label}
                  >
                    <i className={`bi ${item.icon}`}></i>
                    <span>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <button
              type='button'
              className='btn btn-brand-secondary px-3 ciw-mobile-confirm'
              onClick={() => onConfirm(selected)}
              disabled={!canConfirm}
            >
              Conferma
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null

  return (
    <div className='choose-wrestlers-container bg-brand-dark min-vh-100 py-4'>
      <div className='container-fluid px-2 px-md-3'>
        <section className='ciw-hero brand-card border-0 shadow-sm mb-3'>
          <div className='card-body p-3 p-lg-4'>
            <div className='d-flex flex-column flex-xl-row gap-4 justify-content-between'>
              <div className='ciw-hero-copy'>
                <p className='text-light mb-0 ciw-hero-text'>
                  Stai costruendo la tua fazione per partecipare al roster
                  <strong className='text-brand-secondary'>
                    {rosterName ? ` ${rosterName}` : ''}
                  </strong>{' '}
                  rispettando le seguenti condizioni:
                </p>
              </div>
            </div>

            {roster && (
              <div className='ciw-roster-limit-grid mt-3'>
                <div className='ciw-limit-chip'>
                  <span className='ciw-limit-label'>Wrestler</span>
                  <strong>{maxWrestlers}</strong>
                </div>
                <div className='ciw-limit-chip'>
                  <span className='ciw-limit-label'>Budget</span>
                  <strong>{maxCredits} crediti</strong>
                </div>
                {minMaleWrestlers > 0 && (
                  <div className='ciw-limit-chip'>
                    <span className='ciw-limit-label'>Minimo uomini</span>
                    <strong>{minMaleWrestlers}</strong>
                  </div>
                )}
                {minFemaleWrestlers > 0 && (
                  <div className='ciw-limit-chip'>
                    <span className='ciw-limit-label'>Minimo donne</span>
                    <strong>{minFemaleWrestlers}</strong>
                  </div>
                )}
                {(onlyMainRoster || onlyNXT) && (
                  <div className='ciw-limit-chip' style={{ flex: '1 1 100%', minWidth: '200px' }}>
                    <span className='ciw-limit-label'>Brand consentiti</span>
                    <strong>
                      {onlyMainRoster && 'Raw / SmackDown'}
                      {onlyMainRoster && onlyNXT && ' + '}
                      {onlyNXT && 'NXT'}
                    </strong>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <div className='row g-1 align-items-start'>
          <div className='col-12 col-lg-8 col-xl-9 order-2 order-lg-1'>
            <section
              className={`brand-stat rounded-4 shadow-sm ${isMobile ? 'ciw-mobile-filter-bar sticky-top' : 'p-3'
                } mb-3`}
            >
              <div className={isMobile ? 'p-3' : ''}>{filterControls}</div>
            </section>

            <section className='ciw-results-shell'>
              {filteredWrestlers.length === 0 ? (
                <div className='ciw-empty-state text-center py-5'>
                  <i className='bi bi-search display-5 text-muted-brand mb-3'></i>
                  <h5 className='brand-title'>Nessun wrestler trovato</h5>
                  <p className='text-muted-brand mb-3'>
                    Prova a cambiare ricerca o rimuovere i filtri.
                  </p>
                  <button
                    type='button'
                    className='btn btn-outline-brand-secondary'
                    onClick={resetAllFilters}
                  >
                    Rimuovi i filtri
                  </button>
                </div>
              ) : (
                <div className='d-flex flex-column gap-4'>
                  {wrestlerSections.map(section => (
                    <div key={section.key}>
                      {section.title ? (
                        <div className='d-flex align-items-center justify-content-between gap-3 mb-3'>
                          <h5 className='brand-title h6 mb-0 text-light d-flex align-items-center gap-2'>
                            <i
                              className={`bi ${section.key === 'favorites'
                                  ? 'bi-star-fill text-warning'
                                  : 'bi-grid-3x3-gap-fill text-brand-secondary'
                                }`}
                            ></i>
                            {section.title}
                          </h5>
                        </div>
                      ) : null}

                      <div className='row g-3'>
                        {section.wrestlers.map(wrestler => {
                          const isSelected = selectedIds.has(wrestler.id)
                          const disabled =
                            !isSelected &&
                            (selected.length >= maxWrestlers ||
                              (wrestler.price || 0) > remainingCredits)

                          return (
                            <div
                              key={wrestler.id}
                              className='col-6 col-md-4 col-xl-3'
                            >
                              <article
                                className={[
                                  'card h-100 position-relative overflow-hidden wrestler-card ciw-wrestler-card border-0',
                                  isSelected ? 'is-selected' : '',
                                  disabled ? 'is-disabled' : ''
                                ].join(' ')}
                                onClick={() =>
                                  !disabled && toggleWrestler(wrestler)
                                }
                                role='button'
                                tabIndex={disabled ? -1 : 0}
                                onKeyDown={event => {
                                  if (
                                    !disabled &&
                                    (event.key === 'Enter' || event.key === ' ')
                                  ) {
                                    event.preventDefault()
                                    toggleWrestler(wrestler)
                                  }
                                }}
                              >
                                <div className='ciw-card-image'>
                                  <img
                                    src={
                                      wrestler.imageUrl ||
                                      'https://via.placeholder.com/300x300?text=Wrestler'
                                    }
                                    className='position-absolute top-0 start-0 w-100 h-100 object-fit-cover bg-dark'
                                    alt={wrestler.name}
                                    onError={event => {
                                      event.target.onerror = null
                                      event.target.src =
                                        'https://via.placeholder.com/300x300?text=Wrestler'
                                    }}
                                    style={{
                                      height: '160px',
                                      objectFit: 'cover',
                                      objectPosition: 'top center'
                                    }}
                                  />

                                  <div className='ciw-card-topbar'>
                                    <span>
                                      {wrestler.gender === 'Uomo' ? (
                                        <i
                                          className='bi bi-gender-male text-brand-primary-ultralight'
                                          title='Uomo'
                                        ></i>
                                      ) : wrestler.gender === 'Donna' ? (
                                        <i
                                          className='bi bi-gender-female text-brand-accent-light'
                                          title='Donna'
                                        ></i>
                                      ) : (
                                        <i
                                          className='bi bi-gender-ambiguous text-muted-brand'
                                          title={wrestler.gender || 'N/A'}
                                        ></i>
                                      )}
                                    </span>
                                    {isSelected ? (
                                      <span className='ciw-selected-badge'>
                                        <i className='bi bi-check-lg'></i>
                                      </span>
                                    ) : (
                                      <span className='ciw-score-badge'>
                                        {wrestler.score || 0}
                                      </span>
                                    )}
                                  </div>

                                  <div className='ciw-card-bottombar p-1'>
                                    <span
                                      className={`${getBrandBadgeClass(
                                        wrestler.brand
                                      )} badge px-1 py-1 ciw-brand-badge`}
                                      title={wrestler.brand}
                                    >
                                      {wrestler.brand}
                                    </span>
                                    <span className='ciw-price-badge'>
                                      {wrestler.price || 0}{' '}
                                      <CreditIcon size={16} />
                                    </span>
                                  </div>

                                  {favoriteWrestlerIds.includes(wrestler.id) ? (
                                    <div
                                      className='position-absolute'
                                      style={{ top: 46, right: 10, zIndex: 10 }}
                                    >
                                      <span className='text-warning px-2 py-1'>
                                        <i className='bi bi-star-fill'></i>
                                      </span>
                                    </div>
                                  ) : null}

                                  {wrestler.isInjured && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        bottom: 45,
                                        right: 10,
                                        zIndex: 10
                                      }}
                                    >
                                      <InjuryIcon />
                                    </div>
                                  )}

                                  {disabled && !isSelected && (
                                    <div className='ciw-disabled-overlay'>
                                      <div className='text-center'>
                                        <i
                                          className='bi bi-lock-fill text-danger d-block mb-2'
                                          style={{ fontSize: '1.35rem' }}
                                        ></i>
                                        <div className='small text-light fw-semibold'>
                                          {selected.length >= maxWrestlers
                                            ? `Hai gia selezionato ${maxWrestlers} wrestler`
                                            : 'Crediti insufficienti'}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className='card-body d-flex flex-column'>
                                  <div className='ciw-card-name-row'>
                                    <h3
                                      className='card-title h6 mb-0 text-light flex-grow-1'
                                      title={wrestler.name}
                                    >
                                      {wrestler.name}
                                      <span className='ms-1'>
                                    {isPremium && (
                                      <PremiumWrestlerCommentButton wrestler={wrestler}/>
                                    )}
                                      </span>
                                    </h3>
                                  </div>
                                </div>
                              </article>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          <div className='col-12 col-lg-4 col-xl-3 order-1 order-lg-2 d-none d-lg-block'>
            <aside className='summary-sidebar sticky-lg-top'>
              <div className='ciw-desktop-summary-stack mb-3'>
                {summaryItems.map(item => (
                  <div key={item.label} className='ciw-summary-tile'>
                    <div className='ciw-summary-icon'>
                      <i className={`bi ${item.icon}`}></i>
                    </div>
                    <div>
                      <span className='ciw-summary-label'>{item.label}</span>
                      <div className='ciw-summary-value'>{item.value}</div>
                      <small className='text-muted-brand'>{item.helper}</small>
                    </div>
                  </div>
                ))}
              </div>

              <div className='card brand-card border-0 shadow-sm ciw-sidebar-card'>
                <div className='card-header bg-transparent border-0 p-3 pb-0'>
                  <div className='d-flex justify-content-between align-items-center gap-3'>
                    <div>
                      <h2 className='brand-title h5 mb-1 text-light'>
                        Riepilogo selezione
                      </h2>
                      <p className='text-muted-brand small mb-0'>
                        Controlla vincoli, budget e brand prima di confermare.
                      </p>
                    </div>
                    {!isMobile && (
                      <button
                        type='button'
                        className='btn btn-brand-secondary'
                        onClick={() => onConfirm(selected)}
                        disabled={!canConfirm}
                      >
                        Conferma
                      </button>
                    )}
                  </div>
                </div>

                <div className='card-body p-3'>
                  <div className='ciw-progress-block'>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <span className='text-light fw-semibold'>
                        Completamento fazione
                      </span>
                      <span className='text-brand-secondary fw-bold'>
                        {selected.length}/{maxWrestlers}
                      </span>
                    </div>
                    <div className='progress ciw-progress'>
                      <div
                        className='progress-bar bg-brand-secondary'
                        style={{
                          width: `${Math.min(selectionProgress, 100)}%`
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className='ciw-progress-block'>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <span className='text-light fw-semibold'>
                        Budget usato
                      </span>
                      <span
                        className={`fw-bold ${remainingCredits < maxCredits * 0.15
                            ? 'text-danger'
                            : 'text-brand-secondary'
                          }`}
                      >
                        {spentCredits}/{maxCredits}
                      </span>
                    </div>
                    <div className='progress ciw-progress'>
                      <div
                        className={`progress-bar ${spentCredits > maxCredits * 0.9
                            ? 'bg-danger'
                            : 'bg-brand-secondary'
                          }`}
                        style={{ width: `${Math.min(creditsProgress, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className='ciw-pill-grid mb-3'>
                    <span
                      className={`badge rounded-pill px-3 py-2 ${maleCount >= minMaleWrestlers
                          ? 'bg-male'
                          : 'bg-secondary'
                        }`}
                    >
                      Uomini {maleCount}/{minMaleWrestlers}
                    </span>
                    <span
                      className={`badge rounded-pill px-3 py-2 ${femaleCount >= minFemaleWrestlers
                          ? 'bg-female'
                          : 'bg-secondary'
                        }`}
                    >
                      Donne {femaleCount}/{minFemaleWrestlers}
                    </span>
                    <span
                      className={`badge rounded-pill px-3 py-2 ${selected.length > 0
                          ? 'bg-brand-secondary text-dark'
                          : 'bg-secondary'
                        }`}
                    >
                      {remainingCredits} crediti rimasti
                    </span>
                  </div>

                  <div className='mb-3'>
                    <h3 className='h6 text-light mb-2'>
                      Distribuzione per brand
                    </h3>
                    <div className='d-flex flex-wrap gap-2'>
                      {Object.entries(brandCounts).length > 0 ? (
                        Object.entries(brandCounts).map(([brand, count]) => (
                          <span
                            key={brand}
                            className={`${getBrandBadgeClass(
                              brand
                            )} badge rounded-pill px-3 py-2`}
                          >
                            {brand}: {count}
                          </span>
                        ))
                      ) : (
                        <small className='text-muted-brand'>
                          Nessun wrestler selezionato.
                        </small>
                      )}
                    </div>
                  </div>

                  <div className='ciw-sidebar-list'>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <h3 className='h6 text-light mb-0'>Wrestler scelti</h3>
                      {selected.length > 0 && (
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-secondary'
                          onClick={() => setSelected([])}
                        >
                          Svuota
                        </button>
                      )}
                    </div>

                    {selected.length === 0 ? (
                      <div className='ciw-empty-selection'>
                        <i className='bi bi-person-plus'></i>
                        <span>
                          Seleziona i wrestler dalla griglia per vedere il
                          riepilogo.
                        </span>
                      </div>
                    ) : (
                      selected.map(wrestler => (
                        <div key={wrestler.id} className='ciw-selected-item'>
                          <div className='d-flex align-items-center gap-2 min-w-0'>
                            <span
                              className={`badge rounded-pill ${wrestler.gender === 'Uomo'
                                  ? 'bg-male'
                                  : 'bg-female'
                                }`}
                            >
                              {wrestler.gender}
                            </span>
                            <div className='min-w-0'>
                              <div className='text-light text-truncate fw-semibold'>
                                {wrestler.name}
                              </div>
                              <small className='text-muted-brand'>
                                {wrestler.brand}
                              </small>
                            </div>
                          </div>

                          <div className='d-flex align-items-center gap-2'>
                            <span className='badge bg-brand-secondary text-dark'>
                              {wrestler.price || 0}
                            </span>
                            <button
                              type='button'
                              className='btn btn-sm btn-outline-danger'
                              onClick={() => toggleWrestler(wrestler)}
                              aria-label={`Rimuovi ${wrestler.name}`}
                            >
                              <i className='bi bi-x-lg'></i>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
      {mobileActionsBar &&
        ReactDOM.createPortal(mobileActionsBar, document.body)}
    </div>
  )
}
